import type { AuthenticatedSession } from "./authenticated-session";

export const SALE_PERMISSIONS = {
  paymentView: "sale_management_payment_view", paymentUpdate: "sale_management_payment_update",
  configurationView: "global_configuration_view", configurationUpdate: "global_configuration_update",
  leadView: "lead_management_view", leadCreate: "lead_management_create",
  leadUpdate: "lead_management_update", leadDelete: "lead_management_delete",
  leadAssign: "lead_management_assign",
  saleView: "sale_management_view", saleCreate: "sale_management_create",
  saleUpdate: "sale_management_update", saleDelete: "sale_management_delete",
  statusCreate: "sale_management_status_create", statusDelete: "sale_management_status_delete",
  statusMove: "sale_management_status_move",
} as const;
export type SalePermission = typeof SALE_PERMISSIONS[keyof typeof SALE_PERMISSIONS];

// Superadmin sessions receive the expanded list from the backend. Role names,
// group membership and record ownership never imply a permission here.
export function hasSalePermission(session: AuthenticatedSession | null, permission: SalePermission): boolean {
  return Boolean(session && session.authorization?.kind === "staff" &&
    session.permissions?.includes(permission));
}

export function canAccessGroupSummary(session: AuthenticatedSession | null, pathname: string): boolean {
  const match = /^\/sale-support\/(?:sales|tasks)\/([^/]+)(?:\/edit)?\/?$/.exec(pathname);
  try {
    return Boolean(match && session?.authorization?.kind === "group-summary" &&
      decodeURIComponent(match[1]) === session.authorization.saleId);
  } catch { return false; }
}

export class SalePermissionError extends Error {
  response: { status: number; data: { message: string } };
  constructor(message = "Permission denied. Your account cannot perform this action.") {
    super(message);
    this.name = "SalePermissionError";
    this.response = { status: 403, data: { message } };
  }
}

export function saleAuthorizationMessage(failure: unknown, fallback: string): string {
  const error = failure as { response?: { status?: number; data?: { message?: string } }; message?: string };
  if (error?.response?.status === 401) return "Your session has expired. Please sign in again.";
  if (error?.response?.status === 403) return "Permission denied. Your account cannot perform this action. Sign in again if your permissions changed.";
  return error?.response?.data?.message || error?.message || fallback;
}

export function checkSaleRequest(method: string, url: string, session: AuthenticatedSession | null, summaryPage = false, body?: unknown) {
  const path = new URL(url, "https://local.invalid").pathname.replace(/\/$/, "");
  const verb = method.toLowerCase();
  const p = SALE_PERMISSIONS;
  let any: SalePermission[] = [];
  let all: SalePermission[] = [];
  const salePath = /^\/api\/sale-managements(?:\/|$)/.test(path);
  const leadPath = /^\/api\/lead-managements(?:\/|$)/.test(path);
  if (summaryPage && session?.authorization?.kind === "group-summary") {
    const ownSale = `/api/sale-managements/${encodeURIComponent(session.authorization.saleId)}`;
    if (path === ownSale && ["get", "put"].includes(verb)) return;
    if (path === "/api/media/upload" && verb === "post" && body instanceof FormData && body.get("entityType") === "TASK_MANAGEMENT" && body.get("tag") !== "SALE_PAYMENT") return;
  }
  if (/^\/api\/global-configuration(?:\/|$)/.test(path)) {
    all = [verb === "get" ? p.configurationView : p.configurationUpdate];
  } else if (leadPath) {
    if (path.endsWith("/convert")) all = [p.leadUpdate, p.saleCreate];
    else if (path.endsWith("/handover")) all = [p.leadAssign];
    else all = [verb === "get" ? p.leadView : verb === "delete" ? p.leadDelete : verb === "post" ? p.leadCreate : p.leadUpdate];
    if (verb === "post" && path === "/api/lead-managements" && body && typeof body === "object" &&
      "assignedSalesId" in body && body.assignedSalesId) all.push(p.leadAssign);
  } else if (/^\/api\/sale-managements\/statuses(?:\/[^/]+)?$/.test(path)) {
    all = [verb === "get" ? p.saleView : verb === "post" ? p.statusCreate : verb === "delete" ? p.statusDelete : p.statusMove];
  } else if (salePath && !(path === "/api/sale-managements/assignees" && verb === "get")) {
    all = [verb === "get" ? p.saleView : verb === "delete" ? p.saleDelete : verb === "post" && !path.endsWith("/request-missing-information") ? p.saleCreate : p.saleUpdate];
  } else if (path === "/api/sale-dashboard") {
    // This endpoint returns both modules' data in one response.
    all = [p.leadView, p.saleView];
  } else if (path === "/api/media/upload" && verb === "post" && body instanceof FormData) {
    if (body.get("entityType") === "LEAD_MANAGEMENT") any = [p.leadCreate, p.leadUpdate];
    if (body.get("entityType") === "TASK_MANAGEMENT") {
      if (body.get("tag") === "SALE_PAYMENT") all = [p.paymentView, p.paymentUpdate];
      else any = [p.saleCreate, p.saleUpdate];
    }
  }
  if ((all.length && !all.every(code => hasSalePermission(session, code))) ||
    (any.length && !any.some(code => hasSalePermission(session, code)))) throw new SalePermissionError();
}
